import type { ReactNode } from 'react';
import { Box, Card, CardContent, Typography, type CardProps } from '@mui/material';
import { composeSx } from '../../utils/composeSx';

export type HFCardDensity = 'default' | 'compact';

export type HFCardProps = CardProps & {
  /** `default` keeps a roomier 24px inset; app shell cards use the compact default. */
  density?: HFCardDensity;
  /** Optional uppercase section title with accent bar (MVP tearsheet card header). */
  title?: ReactNode;
  /** Optional right-aligned header action (e.g. overflow menu). */
  action?: ReactNode;
};

/** Roomier content inset — 24px all around (MUI spacing 3; 4px grid). */
const HF_CARD_CONTENT_PADDING = 3; // 24px

const COMPACT_CONTENT_SX = {
  pt: 1.5,
  pb: 1.5,
  px: 2,
  '&:last-child': { pb: 1.5 },
} as const;

export function HFCard({ children, sx, density = 'compact', title, action, ...props }: HFCardProps) {
  const contentSx =
    density === 'compact'
      ? COMPACT_CONTENT_SX
      : {
          p: HF_CARD_CONTENT_PADDING,
          '&:last-child': { pb: HF_CARD_CONTENT_PADDING },
        };

  const hasHeader = title != null || action != null;

  return (
    <Card
      variant="outlined"
      sx={composeSx(
        {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        },
        sx,
      )}
      {...props}
    >
      {hasHeader ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            px: density === 'compact' ? 2 : HF_CARD_CONTENT_PADDING,
            pt: density === 'compact' ? 1.5 : HF_CARD_CONTENT_PADDING,
            pb: 0,
          }}
        >
          {title != null ? (
            <Typography
              component="h3"
              sx={{
                m: 0,
                pl: 1.125,
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                color: 'text.primary',
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          ) : (
            <Box />
          )}
          {action != null ? <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{action}</Box> : null}
        </Box>
      ) : null}
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          ...contentSx,
          ...(hasHeader && density === 'default' ? { pt: 1.5 } : null),
          ...(hasHeader && density === 'compact' ? { pt: 1 } : null),
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
}
